import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Download, AlertCircle } from "lucide-react";
import { API } from "@/lib/api";
import axios from "axios";

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_DURATION_MS = 15 * 60 * 1000; // 15 minutes

export default function DossierThanksPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [info, setInfo] = useState(null);
  const [polling, setPolling] = useState(true);
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setPolling(false);
      return;
    }
    let cancelled = false;
    const start = Date.now();

    const tick = async () => {
      if (cancelled) return;
      try {
        const r = await axios.get(`${API}/dossier/by-session/${sessionId}`);
        if (cancelled) return;
        setInfo(r.data);
        setElapsed(Math.floor((Date.now() - start) / 1000));
        if (r.data?.ready || r.data?.status === "error") {
          setPolling(false);
          return;
        }
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Could not check dossier status.");
      }
      if (Date.now() - start < MAX_POLL_DURATION_MS) {
        setTimeout(tick, POLL_INTERVAL_MS);
      } else {
        setPolling(false);
      }
    };

    tick();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const ready = info?.ready;
  const status = info?.status || "paid_pending";
  const dossierId = info?.dossier_id;
  const downloadUrl =
    ready && dossierId
      ? `${API}/dossier/download/${dossierId}?session_id=${encodeURIComponent(sessionId)}`
      : null;

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16" data-testid="dossier-thanks-page">
      <section className="paper-card-elevated rounded-sm p-7 sm:p-9 text-center">
        {ready ? (
          <CheckCircle2 className="w-10 h-10 mx-auto mb-4 text-[hsl(var(--accent-green))]" />
        ) : status === "error" ? (
          <AlertCircle className="w-10 h-10 mx-auto mb-4 text-[hsl(var(--accent-red))]" />
        ) : (
          <Loader2 className="w-10 h-10 mx-auto mb-4 text-[hsl(var(--accent-blue))] animate-spin" />
        )}

        <div className="section-label mb-3">
          {ready
            ? "Dossier ready"
            : status === "error"
            ? "Dossier error"
            : status === "in_progress"
            ? "Generating your dossier"
            : "Dossier request received"}
        </div>

        <h1 className="hero-title text-3xl sm:text-4xl mb-4">
          {ready
            ? "Your dossier is ready."
            : status === "error"
            ? "Something went wrong."
            : "Thank you — your dossier is being prepared."}
        </h1>

        {ready ? (
          <>
            <p className="text-sm sm:text-base text-[hsl(var(--ink-2))] leading-relaxed max-w-xl mx-auto">
              Bookmark this page or save the file below. The link is private and only works with
              your unique session ID. Watermarked and copy-protected per your purchase.
            </p>
            <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
              <a
                href={downloadUrl}
                className="pill-btn pill-btn-primary"
                data-testid="dossier-download-button"
                download
              >
                <Download className="w-4 h-4" />
                Download dossier (PDF)
              </a>
            </div>
            {info?.bill_or_measure && (
              <div className="mt-4 text-[11px] font-mono text-[hsl(var(--ink-muted))]">
                Subject: {info.bill_or_measure}
              </div>
            )}
          </>
        ) : status === "error" ? (
          <>
            <p className="text-sm sm:text-base text-[hsl(var(--ink-2))] leading-relaxed max-w-xl mx-auto">
              We received your payment but the dossier couldn&rsquo;t be auto-generated.
              Please contact support so we can deliver it manually.
            </p>
            {info?.generation_error && (
              <div className="mt-3 text-[11px] font-mono text-[hsl(var(--accent-red))] break-words max-w-xl mx-auto" data-testid="dossier-error-message">
                {info.generation_error}
              </div>
            )}
          </>
        ) : (
          <>
            <p className="text-sm sm:text-base text-[hsl(var(--ink-2))] leading-relaxed max-w-xl mx-auto">
              Most dossiers finish in 1&ndash;3 minutes. Keep this page open and the
              download will appear automatically when ready.
            </p>
            <div className="mt-4 text-[11px] font-mono text-[hsl(var(--ink-muted))]" data-testid="dossier-poll-status">
              {polling ? `Checking… (${elapsed}s elapsed)` : "Polling stopped. Refresh to retry."}
            </div>
            {error && (
              <div className="mt-2 text-[11px] font-mono text-[hsl(var(--accent-red))]" data-testid="dossier-poll-error">
                {error}
              </div>
            )}
          </>
        )}

        {sessionId && (
          <div
            className="mt-6 text-[10px] font-mono text-[hsl(var(--ink-muted))] break-all"
            data-testid="dossier-session-id"
          >
            Session: {sessionId}
          </div>
        )}

        <div className="mt-8 flex items-center justify-center gap-2 flex-wrap">
          <Link to="/measures" className="pill-btn pill-btn-ghost" data-testid="dossier-thanks-measures-link">
            Browse other analyses
          </Link>
          <Link to="/" className="pill-btn pill-btn-ghost" data-testid="dossier-thanks-home-link">
            Start a new scan
          </Link>
        </div>
      </section>
    </div>
  );
}
